import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { commandsApi, interactionsApi, type ComponentData } from '@/lib/commandsApi'
import { pickCandidates, type PickEnv } from '@/lib/commands'
import type { ApplicationCommandIndex, Message } from '@/types/api'
import type { ComponentEnv } from './MessageComponents'

/** Откуда список команд: сервер или личка с ботом; null — команд тут нет (группы, ЛС с человеком). */
export type CommandSource = { guildId: string } | { dmChannelId: string } | null

const EMPTY_INDEX: ApplicationCommandIndex = { applications: [], application_commands: [] }

/** Список команд кэшируется по серверу: во всех его каналах он один (react-query). */
export function indexQuery(source: CommandSource) {
  return {
    queryKey: !source ? ['commands', 'none'] : 'guildId' in source ? ['commands', 'guild', source.guildId] : ['commands', 'dm', source.dmChannelId],
    queryFn: () => (!source ? Promise.resolve(EMPTY_INDEX) : 'guildId' in source ? commandsApi.index(source.guildId) : commandsApi.dmIndex(source.dmChannelId)),
    staleTime: 60_000,
  }
}

/**
 * Кнопки и списки под сообщениями чата: кандидаты для списков участников, ролей и каналов
 * и само нажатие (POST /interactions, type 3).
 */
export function useComponentEnv({ source, guildId, env }: { source: CommandSource; guildId: string | null; env: PickEnv }): ComponentEnv {
  const qc = useQueryClient()
  return useMemo<ComponentEnv>(
    () => ({
      candidates: (kind, query, channelTypes) => pickCandidates(env, kind, query, channelTypes),
      run: async (message: Message, data: ComponentData) => {
        // у ответа на взаимодействие приложение записано в сообщении, у сообщения бота по REST —
        // ищем приложение по его боту в списке команд; не нашли — сервер знает его по автору
        let appId = message.application_id ?? undefined
        if (!appId && source) {
          const index = await qc.ensureQueryData(indexQuery(source)).catch(() => null)
          appId = index?.applications.find((a) => (a.bot_id ?? a.bot?.id) === message.author.id)?.id
        }
        const res = await interactionsApi.run({
          type: 3,
          ...(appId ? { application_id: appId } : {}),
          channel_id: message.channel_id,
          ...(guildId ? { guild_id: guildId } : {}),
          message_id: message.id,
          data,
        })
        if (res.kind !== 'error') return null
        // отказ сервера («Кнопка больше не работает») — его словами, бот не ответил или сбой — как у Discord
        return res.status === 400 || res.status === 403 || res.status === 429 ? res.message : 'Взаимодействие не удалось'
      },
    }),
    [env, source, guildId, qc],
  )
}
